import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { Campaign } from "../../types/sponsorships/campaigns";

const PRO_CAMPAIGN_MIN_BUDGET = 5000;
const EARLY_BIRD_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * TEMPORARY — delete this block once the backend returns `live_date` or `live_at` (early access window).
 * Until then, the Early access section stays empty; this fallback clones the first pro campaigns with fake
 * metadata so you can test the UI. Revert/remove before release.
 */
const TEMP_EARLY_ACCESS_FALLBACK_FOR_API_TESTING = true;
const TEMP_EARLY_ACCESS_FALLBACK_COUNT = 2;

type CampaignEarly = Campaign & { live_date?: string; applicants_count?: number };

const withTempEarlyAccessFields = (campaigns: Campaign[]): CampaignEarly[] =>
    campaigns.map((campaign, index) => ({
        ...campaign,
        live_date: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
        ...(index === 0 ? { applicants_count: 18 } : {}),
    }));

const toNumber = (value: unknown): number => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
};

const isWithinLast24Hours = (liveDate?: string): boolean => {
    if (!liveDate) return false;
    const timestamp = new Date(liveDate).getTime();
    if (Number.isNaN(timestamp)) return false;

    const now = Date.now();
    const diff = now - timestamp;
    return diff >= 0 && diff <= EARLY_BIRD_WINDOW_MS;
};

export const selectAllCampaigns = (state: RootState): Campaign[] => state.campaigns.all_campaigns ?? [];

export const selectPaidCampaigns = createSelector(
    [selectAllCampaigns],
    (campaigns) => campaigns.filter((campaign) => toNumber(campaign?.budget) > 0)
);

export const selectBarterCampaigns = createSelector(
    [selectAllCampaigns],
    (campaigns) => campaigns.filter((campaign) => !(toNumber(campaign?.budget) > 0) && toNumber(campaign?.barter_budget) > 0)
);

export const selectProCampaigns = createSelector(
    [selectAllCampaigns],
    (campaigns) => campaigns.filter((campaign) => toNumber(campaign?.budget) > PRO_CAMPAIGN_MIN_BUDGET)
);

export const selectEarlyBirdCampaigns = createSelector([selectProCampaigns], (campaigns) => {
    const fromApi = campaigns.filter((campaign) =>
        isWithinLast24Hours((campaign as Campaign & { live_date?: string }).live_date)
    );
    if (fromApi.length > 0) return fromApi;

    if (TEMP_EARLY_ACCESS_FALLBACK_FOR_API_TESTING && campaigns.length > 0) {
        return withTempEarlyAccessFields(campaigns.slice(0, TEMP_EARLY_ACCESS_FALLBACK_COUNT));
    }
    return [];
});
